import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CrzFeedResponse } from '@marstein-radar/shared';
import { tableStyle, thStyle, tdStyle, buttonStyle, formatEur } from '../styles';

// Centrálny register zmlúv (Týž. 21, plán §D) — zistené priamym prieskumom
// celého Slovensko.Digital datahub katalógu 2026-07-18. Kostra kopíruje
// AuctionFeedPage.tsx (etablovaný vzor filtrovateľného feedu).
export function CrzFeedPage() {
  const [q, setQ] = useState('');
  const [minAmount, setMinAmount] = useState('');
  // Týž. 21 (prieskum "čo chýba" 2026-07-18) — rezort a dátum podpisu.
  const [departmentName, setDepartmentName] = useState('');
  const [signedFrom, setSignedFrom] = useState('');
  const [signedTo, setSignedTo] = useState('');
  const [page, setPage] = useState(1);
  const [submittedKey, setSubmittedKey] = useState<string | null>('initial');

  const { data: departments } = useQuery<string[]>({
    queryKey: ['crz-departments'],
    queryFn: async () => {
      const res = await fetch('/api/v1/crz/departments');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  const { data, isFetching } = useQuery<CrzFeedResponse>({
    queryKey: ['crz-feed', submittedKey, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (minAmount.trim()) params.set('minAmount', minAmount.trim());
      if (departmentName) params.set('departmentName', departmentName);
      if (signedFrom) params.set('signedFrom', signedFrom);
      if (signedTo) params.set('signedTo', signedTo);
      params.set('page', String(page));
      const res = await fetch(`/api/v1/crz/feed?${params.toString()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: submittedKey !== null,
  });

  function submit(): void {
    setPage(1);
    setSubmittedKey(`${q}|${minAmount}|${departmentName}|${signedFrom}|${signedTo}|${Date.now()}`);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <main className="page">
      <div className="page-header">
        <h1>Zmluvy so štátom</h1>
        <p>
          Centrálny register zmlúv — kto dodáva štátu a za koľko.{' '}
          <Link to="/zmluvy/rizikovi-dodavatelia">Zobraziť rizikových dodávateľov →</Link>
        </p>
      </div>

      <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
        Zdroj: Centrálny register zmlúv (Slovensko.Digital datahub). Zobrazujú sa len zmluvy, kde sa
        aspoň jednu stranu (objednávateľa alebo dodávateľa) podarilo napárovať na firmu v našej
        databáze podľa IČO.
      </div>

      <div className="search-filter-panel">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="filter-grid">
            <label className="filter-field filter-field-wide">
              <span>Hľadať v predmete / dodávateľovi / objednávateľovi</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="napr. IT služby, stavebné práce, názov firmy..."
              />
            </label>
            <label className="filter-field">
              <span>Minimálna suma (€)</span>
              <input
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="napr. 10000"
              />
            </label>
            <label className="filter-field">
              <span>Rezort</span>
              <select value={departmentName} onChange={(e) => setDepartmentName(e.target.value)}>
                <option value="">Všetky rezorty</option>
                {departments?.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field filter-field-narrow">
              <span>Podpis od</span>
              <input
                value={signedFrom}
                onChange={(e) => setSignedFrom(e.target.value)}
                type="date"
              />
            </label>
            <label className="filter-field filter-field-narrow">
              <span>Podpis do</span>
              <input value={signedTo} onChange={(e) => setSignedTo(e.target.value)} type="date" />
            </label>
          </div>
          <button type="submit" className="search-filter-submit">
            Hľadať
          </button>
        </form>
      </div>

      {isFetching && !data && (
        <div className="fin-panel">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="skel-row" key={i}>
              <div>
                <div className="skel line1" />
                <div className="skel line2" />
              </div>
              <div className="skel pill" />
              <div className="skel num" />
            </div>
          ))}
        </div>
      )}
      {data && data.results.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Žiadne zmluvy nenájdené pre zadané filtre.
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <div className="section-title">
            <h2>Výsledky</h2>
            <span className="hint">{data.total.toLocaleString('sk-SK')} zmlúv spolu</span>
          </div>
          <div className="overflow-x">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Dátum podpisu</th>
                  <th style={thStyle}>Objednávateľ</th>
                  <th style={thStyle}>Dodávateľ</th>
                  <th style={thStyle}>Predmet</th>
                  <th style={thStyle} className="amt">
                    Suma
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((c) => (
                  <tr key={c.id}>
                    <td style={tdStyle} className="num">
                      {c.signedOn ?? '—'}
                    </td>
                    <td style={tdStyle}>{c.contractingAuthorityName ?? '—'}</td>
                    <td style={tdStyle}>{c.supplierName ?? '—'}</td>
                    <td style={{ ...tdStyle, maxWidth: 380 }}>{c.subject ?? '—'}</td>
                    <td style={tdStyle} className="amt num">
                      {c.priceAmount ? formatEur(Number(c.priceAmount)) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem' }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ ...buttonStyle, opacity: page <= 1 ? 0.5 : 1 }}
              >
                ← Predošlá
              </button>
              <span style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                Strana {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ ...buttonStyle, opacity: page >= totalPages ? 0.5 : 1 }}
              >
                Ďalšia →
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
